import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import StatusBadge from "../components/StatusBadge";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Upload, CloudArrowUp, Users, Warehouse, ClipboardText, MagnifyingGlass,
  Lock, ArrowClockwise, FileText, CheckCircle, X, UserPlus, Trash, LockKey, LockKeyOpen,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "matched", label: "Matched" },
  { value: "recount_required", label: "Recount Required" },
  { value: "locked_awaiting_head_counter", label: "Locked — Escalated" },
  { value: "finalized_by_head_counter", label: "Finalized" },
];

export default function AdminSessionDetail() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ status: "", matched: "", warehouse: "", location: "", item_code: "", description: "", tonality: "", alley_supervisor: "" });
  const [supervisorOptions, setSupervisorOptions] = useState({ supervisors: [], has_unassigned: false });
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("grid");
  const [historyItem, setHistoryItem] = useState(null);

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [replaceOnCommit, setReplaceOnCommit] = useState(false);
  const [sheetName, setSheetName] = useState("");
  const [committing, setCommitting] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: sum }, { data: as }, { data: us }] = await Promise.all([
      api.get(`/sessions/${id}`),
      api.get(`/sessions/${id}/summary`),
      api.get(`/sessions/${id}/assignments`),
      api.get("/users"),
    ]);
    setSession(s);
    setSummary(sum);
    setAssignments(as);
    setUsers(us);
  };

  const loadItems = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get(`/sessions/${id}/items`, { params });
    setItems(data);
  };

  const loadSupervisors = async () => {
    try {
      const { data } = await api.get(`/sessions/${id}/alley-supervisors`);
      setSupervisorOptions(data);
    } catch {
      // Filter options are non-critical; the grid still works without them.
    }
  };

  useEffect(() => { load(); loadSupervisors(); }, [id]);
  useEffect(() => { loadItems(); }, [id, filters]);

  const counterAssignments = assignments.filter((a) => a.role_in_session === "counter");
  const headCounterAssignments = assignments.filter((a) => a.role_in_session === "head_counter");
  const alleyHeadAssignments = assignments.filter((a) => a.role_in_session === "alley_head");
  const whAlleyAssignments = assignments.filter((a) => a.role_in_session === "wh_alley");
  const counterUsersList = useMemo(() => counterAssignments.map((a) => a.user).filter(Boolean), [assignments]);

  const availableCounters = users.filter((u) => u.role === "counter" && u.is_active && !counterAssignments.some((a) => a.user_id === u.id));
  const availableHeadCounters = users.filter((u) => u.role === "head_counter" && u.is_active && !headCounterAssignments.some((a) => a.user_id === u.id));
  const availableAlleyHeads = users.filter((u) => u.role === "alley_head" && u.is_active && !alleyHeadAssignments.some((a) => a.user_id === u.id));
  const availableWHAlleys = users.filter((u) => u.role === "wh_alley" && u.is_active && !whAlleyAssignments.some((a) => a.user_id === u.id));

  const runPreview = async () => {
    if (!importFile) { toast.error("Choose a file first"); return; }
    const form = new FormData();
    form.append("file", importFile);
    if (sheetName) form.append("sheet_name", sheetName);
    try {
      const { data } = await api.post(`/sessions/${id}/import/preview`, form);
      setPreview(data);
      if (!sheetName) setSheetName(data.sheet_name);
      toast.success(`${data.valid_rows} valid rows · ${data.error_rows} errors`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runCommit = async () => {
    if (!importFile) return;
    setCommitting(true);
    const form = new FormData();
    form.append("file", importFile);
    if (sheetName) form.append("sheet_name", sheetName);
    form.append("replace", replaceOnCommit ? "true" : "false");
    try {
      const { data } = await api.post(`/sessions/${id}/import/commit`, form);
      toast.success(`Imported ${data.inserted} rows (${data.skipped} skipped).`);
      setImportFile(null); setPreview(null); setReplaceOnCommit(false);
      await load(); await loadItems();
      setTab("grid");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  const assignCounters = async (userIds) => {
    if (!userIds.length) return;
    try {
      await api.post(`/sessions/${id}/assignments`, { user_ids: userIds, role_in_session: "counter" });
      toast.success("Counters assigned");
      await load(); await loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const assignHeadCounters = async (userIds) => {
    if (!userIds.length) return;
    try {
      await api.post(`/sessions/${id}/assignments`, { user_ids: userIds, role_in_session: "head_counter" });
      toast.success(userIds.length > 1 ? "Head counters assigned" : "Head counter assigned");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assignAlleyHeads = async (userIds) => {
    if (!userIds.length) return;
    try {
      await api.post(`/sessions/${id}/assignments`, { user_ids: userIds, role_in_session: "alley_head" });
      toast.success(userIds.length > 1 ? "Alley Heads assigned" : "Alley Head assigned");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const assignWHAlleys = async (userIds) => {
    if (!userIds.length) return;
    try {
      await api.post(`/sessions/${id}/assignments`, { user_ids: userIds, role_in_session: "wh_alley" });
      toast.success(userIds.length > 1 ? "WH-Alley users assigned" : "WH-Alley user assigned");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAssignment = async (aid) => {
    try {
      await api.delete(`/sessions/${id}/assignments/${aid}`);
      toast.success("Removed");
      await load(); await loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const escalate = async (itemId) => {
    if (!window.confirm("Manually escalate this SKU to the Head Counter?")) return;
    try {
      await api.post(`/sessions/${id}/items/${itemId}/escalate`);
      toast.success("Escalated");
      await loadItems(); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateItemRemarks = async (it) => {
    const current = it.admin_remarks || it.remarks || "";
    const next = window.prompt("Remarks visible to Alley Head:", current);

    if (next === null) return;

    try {
      await api.patch(`/sessions/${id}/items/${it.id}/remarks`, {
        remarks: next,
      });
      toast.success("Remarks updated");
      await loadItems();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateAlleySupervisor = async (it) => {
    const current = it.alley_supervisor || "";
    const next = window.prompt(
      `Alley Supervisor for ${it.item_code || it.item_description || "this item"}:`,
      current,
    );

    if (next === null) return;

    try {
      await api.patch(`/sessions/${id}/items/${it.id}/alley-supervisor`, {
        alley_supervisor: next,
      });
      toast.success("Alley Supervisor updated");
      await loadItems();
      await loadSupervisors();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleCutoff = async (enable) => {
    const verb = enable ? "Cut off (freeze) all counts and tally blanks as 0?" : "Disable cutoff and allow counters to resume submitting?";
    if (!window.confirm(verb)) return;
    try {
      await api.post(`/sessions/${id}/cutoff`, null, { params: { enable } });
      toast.success(enable ? "Counts cut off. Blank counts tallied as 0." : "Cutoff disabled — counters can resume.");
      await load(); await loadItems();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async (item) => {
    const { data } = await api.get(`/sessions/${id}/items/${item.id}/history`);
    setHistoryItem({ item, entries: data });
  };

  if (!session) return <AppShell><div className="p-10 text-zinc-500">Loading…</div></AppShell>;

  const statusRowClass = (status) =>
    status === "matched" ? "row-matched" :
    status === "recount_required" ? "row-recount" :
    status === "locked_awaiting_head_counter" ? "row-locked" :
    status === "finalized_by_head_counter" ? "row-final" : "";

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <Link to="/admin/sessions" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"><ArrowLeft className="w-3.5 h-3.5" /> All sessions</Link>
            <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight mt-1">{session.session_name}</h1>
            <div className="text-sm text-zinc-600 mono">
              {session.warehouse_name || "—"} · <span className="uppercase tracking-wider text-xs">{session.session_status}</span> · {session.item_count} SKUs
              {session.is_cutoff && <span className="ml-2 inline-flex items-center gap-1 rounded-sm border border-zinc-400 bg-zinc-200 px-2 py-0.5 text-[11px] text-zinc-800 uppercase tracking-wider"><LockKey className="w-3 h-3" /> Cutoff</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {session.session_status === "active" && (
              session.is_cutoff ? (
                <button onClick={() => toggleCutoff(false)} className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-400 bg-white hover:bg-zinc-100 text-zinc-900 px-3 py-2 text-sm" data-testid="disable-cutoff-button">
                  <LockKeyOpen className="w-4 h-4" /> Disable Cutoff
                </button>
              ) : (
                <button onClick={() => toggleCutoff(true)} className="inline-flex items-center gap-1.5 rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-sm" data-testid="cutoff-button">
                  <LockKey className="w-4 h-4" /> Cutoff
                </button>
              )
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-zinc-300 gap-1">
          {[
            { id: "grid",        label: "Reconciliation Grid", icon: ClipboardText },
            { id: "import",      label: "Import Excel",         icon: Upload },
            { id: "assignments", label: "Assignments",          icon: Users },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? "border-indigo-900 text-zinc-900 font-medium" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "grid" && (
          <>
            {/* Filter bar */}
            <div className="rounded-sm border border-zinc-300 bg-white p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <select className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" value={filters.matched} onChange={(e) => setFilters({ ...filters, matched: e.target.value })} data-testid="filter-matched">
                <option value="">All results</option>
                <option value="matched">Matched only</option>
                <option value="discrepancy">Discrepancy only</option>
              </select>
              <input className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" placeholder="Warehouse" value={filters.warehouse} onChange={(e) => setFilters({ ...filters, warehouse: e.target.value })} data-testid="filter-warehouse" />
              <input className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" placeholder="Item code" value={filters.item_code} onChange={(e) => setFilters({ ...filters, item_code: e.target.value })} data-testid="filter-item-code" />
              <input className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" placeholder="Description" value={filters.description} onChange={(e) => setFilters({ ...filters, description: e.target.value })} data-testid="filter-description" />
              <input className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" placeholder="Tonality" value={filters.tonality} onChange={(e) => setFilters({ ...filters, tonality: e.target.value })} data-testid="filter-tonality" />
              <select className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" value={filters.alley_supervisor} onChange={(e) => setFilters({ ...filters, alley_supervisor: e.target.value })} data-testid="filter-alley-supervisor">
                <option value="">All Alley Supervisors</option>
                {supervisorOptions.supervisors.map((n) => <option key={n} value={n}>{n}</option>)}
                {supervisorOptions.has_unassigned && <option value="__unassigned__">— Unassigned —</option>}
              </select>
              <input className="rounded-sm border border-zinc-300 px-2 py-1.5 text-sm" placeholder="Location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="filter-location" />
            </div>

            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                {["total","matched","recount_required","pending","locked_awaiting_head_counter","finalized_by_head_counter"].map((k, i) => (
                  <div key={k} className="rounded-sm border border-zinc-300 bg-white px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, " ")}</div>
                    <div className="font-mono text-lg">{k === "total" ? summary.total : summary.by_status[k]}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th className="hidden md:table-cell">Warehouse</th>
                      <th className="hidden lg:table-cell">Size</th>
                      <th>Item Code</th>
                      <th className="hidden sm:table-cell">Description</th>
                      <th className="hidden xl:table-cell">Location</th>
                      <th className="hidden xl:table-cell">Tonality</th>
                      <th>Alley Supervisor</th>
                      <th className="text-right hidden lg:table-cell">pcs/box</th>
                      <th className="text-right hidden xl:table-cell">Price</th>
                      <th className="text-right">System</th>
                      {counterUsersList.map((u) => (
                        <th key={u.id} className="text-right hidden md:table-cell" title={u.full_name}>{u.full_name.split(" ")[0]}</th>
                      ))}
                      <th className="text-right">Collective</th>
                      <th className="text-right">Disc.</th>
                      <th className="text-right hidden lg:table-cell">Final</th>
                      <th className="text-right hidden lg:table-cell">Final Disc.</th>
                      <th className="hidden xl:table-cell">Reason</th>
                      <th>Admin Remarks</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const r = it.reconciliation || {};
                      const rowCls = statusRowClass(r.status);
                      return (
                        <tr key={it.id} className={rowCls} data-testid={`recon-row-${it.id}`}>
                          <td><StatusBadge status={r.status || "pending"} direction={r.discrepancy > 0 ? "over" : r.discrepancy < 0 ? "short" : undefined} /></td>
                          <td className="mono hidden md:table-cell">{it.warehouse_code}</td>
                          <td className="mono hidden lg:table-cell">{it.size}</td>
                          <td className="mono font-medium">
                            {it.item_code}
                            <div className="sm:hidden text-xs text-zinc-600 truncate max-w-[180px]">{it.item_description}</div>
                          </td>
                          <td className="min-w-[180px] hidden sm:table-cell">{it.item_description}</td>
                          <td className="mono hidden xl:table-cell">{it.location || "—"}</td>
                          <td className="mono hidden xl:table-cell">{it.tonality || "—"}</td>
                          <td className="text-xs whitespace-nowrap">
                            <div>{it.alley_supervisor || <span className="text-zinc-400">—</span>}</div>
                            <button
                              onClick={() => updateAlleySupervisor(it)}
                              className="mt-1 inline-flex items-center rounded-sm border border-zinc-300 hover:bg-zinc-100 px-2 py-0.5 text-[11px]"
                              data-testid={`edit-supervisor-${it.id}`}
                            >
                              Edit
                            </button>
                          </td>
                          <td className="text-right mono hidden lg:table-cell">{it.pcs_per_box ?? "—"}</td>
                          <td className="text-right mono hidden xl:table-cell">{it.price ?? "—"}</td>
                          <td className="text-right mono">{it.system_count}</td>
                          {counterUsersList.map((u) => {
                            const c = it.counters.find((cc) => cc.user_id === u.id);
                            const val = c?.latest_count;
                            return (
                              <td key={u.id} className="text-right mono hidden md:table-cell" title={c?.remarks || ""}>
                                <div>{val ?? "—"}</div>
                                <div className="text-[10px] text-zinc-500">{c ? `${c.attempts_used}/5` : "0/5"}</div>
                              </td>
                            );
                          })}
                          <td className="text-right mono font-medium">{r.collective_count ?? 0}</td>
                          <td className={`text-right mono ${r.discrepancy === 0 ? "disc-match" : "disc-over"}`}>{r.discrepancy ?? 0}{r.discrepancy > 0 ? " ▲" : r.discrepancy < 0 ? " ▼" : ""}</td>
                          <td className="text-right mono hidden lg:table-cell">{r.final_head_count ?? "—"}</td>
                          <td className="text-right mono hidden lg:table-cell">{r.final_discrepancy ?? "—"}</td>
                          <td className="text-xs text-zinc-600 hidden xl:table-cell">{r.final_reason || ""}</td>
                          <td className="text-xs min-w-[220px] max-w-[360px] whitespace-normal break-words">
                            <div>{it.admin_remarks || it.remarks || <span className="text-zinc-400">—</span>}</div>
                            <button
                              onClick={() => updateItemRemarks(it)}
                              className="mt-1 inline-flex items-center rounded-sm border border-zinc-300 hover:bg-zinc-100 px-2 py-0.5 text-[11px]"
                            >
                              Edit Remarks
                            </button>
                          </td>
                          <td className="text-right whitespace-nowrap space-x-1">
                            <button onClick={() => openHistory(it)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-zinc-300 hover:bg-zinc-100" data-testid={`history-${it.id}`}><ClipboardText className="w-3.5 h-3.5" /> History</button>
                            {r.status !== "locked_awaiting_head_counter" && r.status !== "finalized_by_head_counter" && r.status !== "matched" && (
                              <button onClick={() => escalate(it.id)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-zinc-400 hover:bg-zinc-100" data-testid={`escalate-${it.id}`}><Lock className="w-3.5 h-3.5" /> Escalate</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {items.length === 0 && (
                      <tr><td colSpan={11 + counterUsersList.length + 7} className="text-center py-10 text-zinc-500">No items match your filters. {session.item_count === 0 && "Import an Excel file to begin."}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "import" && (
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1 rounded-sm border border-zinc-300 bg-white p-4 space-y-3">
              <div className="font-display font-semibold text-zinc-900">Upload Excel</div>
              <p className="text-sm text-zinc-600">Supported headers: Warehouse Code, Size, Item Code, Item Description, Location, Tonality, pcs/box, Price, Stock on hand, Alley Supervisor.</p>
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-300 rounded-sm py-8 cursor-pointer hover:bg-zinc-50" data-testid="file-drop">
                <CloudArrowUp className="w-6 h-6 text-zinc-500" />
                <div className="text-sm text-zinc-700">{importFile ? importFile.name : "Click to choose .xlsx / .xls"}</div>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { setImportFile(e.target.files[0]); setPreview(null); }} data-testid="import-file-input" />
              </label>
              <input className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm" placeholder="Sheet name (optional)" value={sheetName} onChange={(e) => setSheetName(e.target.value)} data-testid="import-sheet-name" />
              <div className="flex gap-2">
                <button onClick={runPreview} className="flex-1 rounded-sm border border-zinc-300 hover:bg-zinc-100 px-3 py-2 text-sm inline-flex items-center justify-center gap-1.5" data-testid="import-preview-button"><MagnifyingGlass className="w-4 h-4" /> Preview</button>
              </div>
              {preview && (
                <div className="space-y-2">
                  <div className="flex items-center gap-4 text-sm">
                    <span>Total: <b className="mono">{preview.total_rows}</b></span>
                    <span className="text-emerald-700">Valid: <b className="mono">{preview.valid_rows}</b></span>
                    <span className="text-rose-700">Errors: <b className="mono">{preview.error_rows}</b></span>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={replaceOnCommit} onChange={(e) => setReplaceOnCommit(e.target.checked)} data-testid="import-replace-toggle" /> Replace existing items</label>
                  <button onClick={runCommit} disabled={committing || preview.valid_rows === 0} className="w-full rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-60 text-white px-3 py-2 text-sm inline-flex items-center justify-center gap-1.5" data-testid="import-commit-button">
                    <Upload className="w-4 h-4" /> {committing ? "Importing…" : `Import ${preview.valid_rows} rows`}
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-2 rounded-sm border border-zinc-300 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-200 font-display font-semibold text-zinc-900">Preview</div>
              <div className="max-h-[70vh] overflow-auto">
                {!preview ? (
                  <div className="p-8 text-center text-zinc-500 text-sm">Upload a file and click Preview to see rows.</div>
                ) : (
                  <table className="data-table">
                    <thead><tr><th>Row</th><th>WH</th><th>Size</th><th>Code</th><th>Description</th><th>Location</th><th>Tonality</th><th className="text-right">pcs/box</th><th className="text-right">Price</th><th className="text-right">System</th><th>Alley Supervisor</th><th>Errors</th></tr></thead>
                    <tbody>
                      {preview.rows.map((r, i) => (
                        <tr key={i} className={r.errors.length ? "row-recount" : ""}>
                          <td className="mono">{r.row_number}</td>
                          <td className="mono">{r.warehouse_code}</td>
                          <td className="mono">{r.size}</td>
                          <td className="mono">{r.item_code}</td>
                          <td>{r.item_description}</td>
                          <td className="mono">{r.location}</td>
                          <td className="mono">{r.tonality}</td>
                          <td className="text-right mono">{r.pcs_per_box ?? "—"}</td>
                          <td className="text-right mono">{r.price ?? "—"}</td>
                          <td className="text-right mono">{r.system_count ?? "—"}</td>
                          <td>{r.alley_supervisor || <span className="text-zinc-400">—</span>}</td>
                          <td className="text-xs text-rose-700">{r.errors.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "assignments" && (
          <div className="grid lg:grid-cols-3 gap-5">
            <div className="rounded-sm border border-zinc-300 bg-white p-4">
              <div className="font-display font-semibold text-zinc-900">Counters</div>
              <p className="text-sm text-zinc-600 mb-3">All counters assigned below will count every SKU in this session. The system sums their latest counts per SKU.</p>
              <ul className="divide-y divide-zinc-200 mb-4">
                {counterAssignments.length === 0 && <li className="text-sm text-zinc-500 py-6 text-center">No counters assigned yet.</li>}
                {counterAssignments.map((a) => (
                  <li key={a.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.user?.full_name}</div>
                      <div className="text-xs text-zinc-500 mono">{a.user?.email}</div>
                    </div>
                    <button onClick={() => removeAssignment(a.id)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`remove-assignment-${a.id}`}><Trash className="w-3.5 h-3.5" /> Remove</button>
                  </li>
                ))}
              </ul>
              <AddCounters available={availableCounters} onAdd={assignCounters} />
            </div>

            <div className="rounded-sm border border-zinc-300 bg-white p-4">
              <div className="font-display font-semibold text-zinc-900">Head Counters</div>
              <p className="text-sm text-zinc-600 mb-3">Head counters finalize any SKU that remains unresolved after all counters exhaust their attempts. You can assign more than one.</p>
              <ul className="divide-y divide-zinc-200 mb-4">
                {headCounterAssignments.length === 0 && <li className="text-sm text-zinc-500 py-6 text-center">No head counter assigned yet.</li>}
                {headCounterAssignments.map((a) => (
                  <li key={a.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.user?.full_name}</div>
                      <div className="text-xs text-zinc-500 mono">{a.user?.email}</div>
                    </div>
                    <button onClick={() => removeAssignment(a.id)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`remove-head-counter-${a.id}`}><Trash className="w-3.5 h-3.5" /> Remove</button>
                  </li>
                ))}
              </ul>
              <AddHeadCounters available={availableHeadCounters} onAdd={assignHeadCounters} />
            </div>

            <div className="rounded-sm border border-zinc-300 bg-white p-4">
              <div className="font-display font-semibold text-zinc-900">Alley Heads</div>
              <p className="text-sm text-zinc-600 mb-3">Alley Heads perform Daily Movement / wall-to-wall recounts. Multiple Alley Heads can be assigned to one session.</p>
              <ul className="divide-y divide-zinc-200 mb-4">
                {alleyHeadAssignments.length === 0 && <li className="text-sm text-zinc-500 py-6 text-center">No Alley Head assigned yet.</li>}
                {alleyHeadAssignments.map((a) => (
                  <li key={a.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.user?.full_name}</div>
                      <div className="text-xs text-zinc-500 mono">{a.user?.email}</div>
                    </div>
                    <button onClick={() => removeAssignment(a.id)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`remove-alley-head-${a.id}`}><Trash className="w-3.5 h-3.5" /> Remove</button>
                  </li>
                ))}
              </ul>
              <AddAlleyHeads available={availableAlleyHeads} onAdd={assignAlleyHeads} />
            </div>

            <div className="rounded-sm border border-zinc-300 bg-white p-4">
              <div className="font-display font-semibold text-zinc-900">WH-Alley (view only)</div>
              <p className="text-sm text-zinc-600 mb-3">WH-Alley users get a read-only list of escalated SKUs showing the item name and its discrepancy only. They cannot submit or finalize anything.</p>
              <ul className="divide-y divide-zinc-200 mb-4">
                {whAlleyAssignments.length === 0 && <li className="text-sm text-zinc-500 py-6 text-center">No WH-Alley user assigned yet.</li>}
                {whAlleyAssignments.map((a) => (
                  <li key={a.id} className="py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.user?.full_name}</div>
                      <div className="text-xs text-zinc-500 mono">{a.user?.email}</div>
                    </div>
                    <button onClick={() => removeAssignment(a.id)} className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`remove-wh-alley-${a.id}`}><Trash className="w-3.5 h-3.5" /> Remove</button>
                  </li>
                ))}
              </ul>
              <AddWHAlleys available={availableWHAlleys} onAdd={assignWHAlleys} />
            </div>
          </div>
        )}
      </div>

      {/* Item history dialog */}
      <Dialog open={!!historyItem} onOpenChange={(o) => !o && setHistoryItem(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{historyItem?.item?.item_code} — Attempt history</DialogTitle></DialogHeader>
          <div className="max-h-[55vh] overflow-auto">
            <table className="data-table">
              <thead><tr><th>At</th><th>Counter</th><th className="text-right">Attempt</th><th className="text-right">Count</th><th>Remarks</th><th>Latest?</th></tr></thead>
              <tbody>
                {(historyItem?.entries || []).map((e) => (
                  <tr key={e.id}>
                    <td className="mono text-xs">{new Date(e.submitted_at).toLocaleString()}</td>
                    <td>{e.user?.full_name} <span className="text-xs text-zinc-500 mono">{e.user?.email}</span></td>
                    <td className="text-right mono">{e.attempt_number}</td>
                    <td className="text-right mono">{e.counted_quantity}</td>
                    <td className="text-xs text-zinc-700 max-w-[260px]">{e.remarks || <span className="text-zinc-400">—</span>}</td>
                    <td>{e.is_latest ? <span className="badge-matched rounded-sm px-1.5 py-0.5 text-[11px]">latest</span> : <span className="text-zinc-400 text-[11px]">superseded</span>}</td>
                  </tr>
                ))}
                {(historyItem?.entries || []).length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-8 text-sm">No submissions yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter><button onClick={() => setHistoryItem(null)} className="rounded-sm border border-zinc-300 px-3 py-1.5 text-sm">Close</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function AddCounters({ available, onAdd }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  if (!available.length) return <div className="text-xs text-zinc-500">No more counters to assign.</div>;
  return (
    <div className="border-t border-zinc-200 pt-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Add counters</div>
      <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
        {available.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`assign-counter-check-${u.id}`} />
            <span>{u.full_name} <span className="text-xs text-zinc-500 mono">— {u.email}</span></span>
          </label>
        ))}
      </div>
      <button onClick={() => { onAdd(selected); setSelected([]); }} disabled={!selected.length}
        className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-50 text-white px-3 py-1.5 text-sm" data-testid="assign-counters-button">
        <UserPlus className="w-4 h-4" /> Add selected
      </button>
    </div>
  );
}

function AddHeadCounters({ available, onAdd }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  if (!available.length) return <div className="text-xs text-zinc-500">No more head counters to assign.</div>;
  return (
    <div className="border-t border-zinc-200 pt-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Add head counters</div>
      <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
        {available.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`assign-head-counter-check-${u.id}`} />
            <span>{u.full_name} <span className="text-xs text-zinc-500 mono">— {u.email}</span></span>
          </label>
        ))}
      </div>
      <button onClick={() => { onAdd(selected); setSelected([]); }} disabled={!selected.length}
        className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-50 text-white px-3 py-1.5 text-sm" data-testid="assign-head-counters-button">
        <UserPlus className="w-4 h-4" /> Add selected
      </button>
    </div>
  );
}

function AddWHAlleys({ available, onAdd }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  if (!available.length) return <div className="text-xs text-zinc-500">No more WH-Alley users to assign.</div>;
  return (
    <div className="border-t border-zinc-200 pt-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Add WH-Alley users</div>
      <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
        {available.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`assign-wh-alley-check-${u.id}`} />
            <span>{u.full_name} <span className="text-xs text-zinc-500 mono">— {u.email}</span></span>
          </label>
        ))}
      </div>
      <button onClick={() => { onAdd(selected); setSelected([]); }} disabled={!selected.length}
        className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-50 text-white px-3 py-1.5 text-sm" data-testid="assign-wh-alleys-button">
        <UserPlus className="w-4 h-4" /> Add selected
      </button>
    </div>
  );
}

function AddAlleyHeads({ available, onAdd }) {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  if (!available.length) return <div className="text-xs text-zinc-500">No more Alley Heads to assign.</div>;
  return (
    <div className="border-t border-zinc-200 pt-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Add Alley Heads</div>
      <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
        {available.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`assign-alley-head-check-${u.id}`} />
            <span>{u.full_name} <span className="text-xs text-zinc-500 mono">— {u.email}</span></span>
          </label>
        ))}
      </div>
      <button onClick={() => { onAdd(selected); setSelected([]); }} disabled={!selected.length}
        className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-50 text-white px-3 py-1.5 text-sm" data-testid="assign-alley-heads-button">
        <UserPlus className="w-4 h-4" /> Add selected
      </button>
    </div>
  );
}

